/**
 * src/components/NavigationShell.jsx â€” Main Navigation
 * AC Nautitech Track B
 *
 * CEO: Focus, Projects, Pipeline, Team, Billing
 * Other roles: filtered tabs per visibility matrix
 */

import { useState, useEffect } from 'react';
import { CheckSquare, Columns, TrendingUp, Users, Users2, CircleDollarSign, CalendarDays, ListChecks, Settings, BarChart3, Contact2, Package, Sun, Clock, MoreHorizontal, X, MessageSquare, LayoutDashboard, Compass, Brain, Languages, Fingerprint, FileText, LogOut, Receipt } from 'lucide-react';
import useBreakpoint from '../hooks/useBreakpoint';
import { useT } from '../lib/i18n/I18nContext';
import SmartSearchOverlay from './shared/SmartSearchOverlay';
import OfflineModeButton from './shared/OfflineModeButton';
import LeadSyncButton from './shared/LeadSyncButton';
import ComposeEmailButton from './shared/ComposeEmailButton';
import RejectedLeadsButton from './shared/RejectedLeadsButton';
import NavTabIcon from './NavTabIcon';

// Role â†’ visible tabs (full list â€” desktop/tablet show all, mobile shows subset + More)
// EVERY role includes 'settings' at the end so every user can at minimum
// change their language/theme/personal preferences. The CEO-only sections
// (role switcher, system health, schema editors) are gated inside
// SettingsView by authRole, not by tab visibility.
const TAB_CONFIG = {
  ceo:        ['command', 'focus', 'tasks', 'projects', 'pipeline', 'procurement', 'directory', 'worklogs', 'workday', 'analytics', 'knowledge', 'interpreter', 'agent', 'bookkeeping', 'mis-documentos', 'settings'],
  designer:   ['tasks', 'projects', 'pipeline', 'workday', 'timeline', 'mis-documentos', 'settings'],
  production: ['tasks', 'projects', 'directory', 'worklogs', 'workday', 'interpreter', 'timeline', 'mis-documentos', 'settings'],
  shop:       ['tasks', 'timeline', 'worklogs', 'workday', 'procurement', 'interpreter', 'mis-documentos', 'settings'],
  pmo:        ['command', 'focus', 'tasks', 'projects', 'pipeline', 'operations', 'procurement', 'directory', 'workday', 'interpreter', 'agent', 'mis-documentos', 'settings'],
  admin:      ['projects', 'pipeline', 'worklogs', 'workday', 'billing', 'mis-documentos', 'settings'],
  sales:      ['pipeline', 'directory', 'tasks', 'workday', 'mis-documentos', 'settings'],
  installer:  ['tasks', 'timeline', 'worklogs', 'workday', 'mis-documentos', 'settings'],
};

// Mobile: first N tabs visible in bottom bar, rest go to More drawer
const MOBILE_VISIBLE_COUNT = {
  ceo: 5,       // Command, Focus, Planner, Projects, Pipeline + More
  designer: 4,
  production: 4,
  shop: 4,
  pmo: 5,       // Command, Focus, Planner, Projects, Pipeline + More
  admin: 4,
  sales: 3,
  installer: 3,
};

const TAB_META = {
  command:  { labelKey: 'nav.command',  icon: Compass },
  focus:    { labelKey: 'nav.focus',    icon: CheckSquare },
  tasks:    { labelKey: 'nav.planner',  icon: ListChecks },
  projects: { labelKey: 'nav.projects', icon: Columns },
  pipeline: { labelKey: 'nav.pipeline', icon: TrendingUp },
  directory: { labelKey: 'nav.directory', icon: Contact2 },
  billing:  { labelKey: 'nav.billing',  icon: CircleDollarSign },
  procurement: { labelKey: 'nav.procurement', icon: Package },
  analytics: { labelKey: 'nav.analytics', icon: BarChart3 },
  settings: { labelKey: 'nav.settings', icon: Settings },
  timeline: { labelKey: 'nav.timeline', icon: CalendarDays },
  worklogs: { labelKey: 'nav.worklogs', icon: Clock },
  'mis-documentos': { labelKey: 'nav.misDocumentos', icon: FileText },
  operations: { labelKey: 'nav.operations', icon: LayoutDashboard },
  knowledge: { labelKey: 'nav.knowledge', icon: Brain },
  interpreter: { labelKey: 'nav.interpreter', icon: Languages },
  workday:  { labelKey: 'nav.workday',  icon: Fingerprint },
  agent:    { labelKey: 'nav.agent',    icon: MessageSquare },
  bookkeeping: { labelKey: 'nav.bookkeeping', icon: Receipt },
};

export default function NavigationShell({ role = 'ceo', realRole, activeTab, onTabChange, onSignOut, children }) {
  // Derive tab list from the OVERRIDE role (role prop = effectiveRole from
  // AppShell). Then, if the user's REAL role is CEO, always append the
  // Settings tab so the CEO can navigate back to Settings â†’ Role Switcher
  // and clear the override. Without this escape hatch, a CEO who overrode
  // to a non-settings role (e.g. production) would be permanently stuck
  // because the override persists in localStorage across reloads.
  // Fixed 2026-04-08 PM after CEO reported "I used the role switcher for
  // the first time, now I'm stuck in production and can't come back."
  const baseTabs = TAB_CONFIG[role] || TAB_CONFIG.ceo;
  const tabs = (realRole === 'ceo' && !baseTabs.includes('settings'))
    ? [...baseTabs, 'settings']
    : baseTabs;
  const { isMobile, isTablet, isDesktop, sidebarWidth } = useBreakpoint();
  const { t } = useT();
  const [moreOpen, setMoreOpen] = useState(false);

  // Sign out — reused in the desktop sidebar profile block and the mobile More
  // drawer. Was missing entirely: signOut existed in AuthContext but no UI called it.
  // AppShell passes onSignOut (it owns the auth context); presentational here so the
  // NavigationShell tests keep rendering it without an AuthProvider.
  const signOutBtn = onSignOut ? (
    <button
      data-testid="nav-sign-out"
      onClick={onSignOut}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        width: '100%', minHeight: 44, padding: '8px 12px', borderRadius: 10,
        background: 'var(--glass)', border: 'none', outline: '1px solid var(--bdr)',
        color: 'var(--tm)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        fontFamily: 'inherit',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--bad)'; e.currentTarget.style.outlineColor = 'var(--bad)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--tm)'; e.currentTarget.style.outlineColor = 'var(--bdr)'; }}
    >
      <LogOut size={16} strokeWidth={1.5} /> {t('nav.signOut')}
    </button>
  ) : null;

  // Icon-only sign-out for the 64px tablet rail (labeled variant doesn't fit).
  // marginTop:auto pins it to the bottom of the fixed full-height column so it
  // mirrors desktop's "under the profile" placement. CEO 2026-07-03: sign-out
  // was absent on the iPad layout (present on desktop + mobile only).
  const signOutIconBtn = onSignOut ? (
    <button
      data-testid="nav-sign-out"
      onClick={onSignOut}
      title={t('nav.signOut')}
      aria-label={t('nav.signOut')}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 44, height: 44, borderRadius: 10, marginTop: 'auto',
        background: 'transparent', border: 'none', color: 'var(--t2)',
        cursor: 'pointer', fontFamily: 'inherit',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--bad)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--t2)'; }}
    >
      <LogOut size={20} strokeWidth={1.5} />
    </button>
  ) : null;

  // TICKET-205 (CEO 2026-06-16): the Gmail lead-sync trigger lives in the top
  // bar NEXT TO the airplane button, but ONLY on the Pipeline tab (where the
  // synced leads land). TICKET-209 (CEO 2026-06-18): the sync TRIGGER is extended
  // to PMO (Agustina) so she can pull new RFQ leads herself - she manages the
  // pipeline. The server mirrors this (api/data.js routeLeads gates
  // leads.sync_gmail to pmo too).
  const showLeadSync = activeTab === 'pipeline' && (realRole === 'ceo' || role === 'ceo' || role === 'admin' || role === 'pmo');
  // Compose + rejected-lead triage stay exec-only (ceo/admin): they reach the
  // mailbox / send on the company behalf. NOT extended to PMO.
  const showLeadManage = activeTab === 'pipeline' && (realRole === 'ceo' || role === 'ceo' || role === 'admin');

  // Redirect removed tabs to their new homes
  useEffect(() => {
    if (activeTab === 'myday') onTabChange('focus');
    else if (activeTab === 'crmcontacts') onTabChange('directory');
    else if (activeTab === 'team') onTabChange('directory');
    else if (!tabs.includes(activeTab)) onTabChange(tabs[0]);
  }, [role, activeTab]);

  // Close More drawer when tab changes
  useEffect(() => { setMoreOpen(false); }, [activeTab]);

  // CEO directive 2026-04-27: search no longer rendered as a fixed-position
  // overlay at viewport top-right. Inlined into each view's page header
  // (next to "New Project" / "New Opportunity") so it sits inside the
  // visible content frame instead of in the reserved-padding gap.
  // Mobile retains its in-flow top-bar render below. Other desktop/tablet
  // views can render <SmartSearchOverlay /> in their own headers as needed.
  const searchOverlay = null;

  if (isDesktop) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        {searchOverlay}
        {/* Sidebar */}
        <nav style={{
          width: 240, flexShrink: 0, background: 'var(--glass)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRight: '1px solid var(--bdr)', padding: '20px 12px',
          display: 'flex', flexDirection: 'column', gap: 4,
          position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 300,
        }}>
          {/* Logo â€” title gets the full row width (no buttons crowding it) */}
          <div style={{
            padding: '8px 12px 12px', display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, background: 'var(--navy-surface)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--on-navy)', fontSize: 12, fontWeight: 700, flexShrink: 0,
            }}>AC</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--t1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>AC Nautitech</div>
              <div style={{ fontSize: 11, color: 'var(--tm)' }}>{role.toUpperCase()}</div>
            </div>
          </div>
          {/* Sign out — right under the profile identity (CEO 2026-07-02: "there is no logout button"). */}
          <div style={{ padding: '0 4px 12px' }}>{signOutBtn}</div>
          {/* Top-bar actions on their OWN row, right-aligned, so the mail +
              airplane buttons don't crowd the title or overlap the search
              field (CEO 2026-06-16, TICKET-205 follow-up). */}
          <div style={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8,
            padding: '0 4px 12px', borderBottom: '1px solid var(--bdr)', marginBottom: 10,
          }}>
            {showLeadSync && <LeadSyncButton />}
            {showLeadManage && <ComposeEmailButton />}
            {showLeadManage && <RejectedLeadsButton />}
            {/* Panel opens INTO content (align=left): the 240px sidebar is
                narrower than the 290px panel, so right:0 from a flex-end button
                pushes it off the left viewport edge (CEO 2026-07-14). */}
            <OfflineModeButton align="left" />
          </div>

          {/* TICKET-107 (c) â€” always-visible finding bar (CEO-approved UX
              2026-06-12: visible field on desktop, icon on mobile/tablet).
              Replaces the bare magnifier that used to sit in the logo row.
              Ctrl+K still toggles the overlay. */}
          <div style={{ padding: '0 0 8px' }}>
            <SmartSearchOverlay variant="field" />
          </div>

          {tabs.map(tab => {
            const meta = TAB_META[tab];
            const active = activeTab === tab;
            const Icon = meta.icon;
            return (
              <button
                key={tab}
                onClick={() => onTabChange(tab)}
                data-testid={`nav-tab-${tab}`}
                aria-label={t(meta.labelKey)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 10, width: '100%',
                  background: active ? 'var(--accent-bg)' : 'transparent',
                  color: active ? 'var(--accent)' : 'var(--t2)',
                  fontSize: 14, fontWeight: active ? 600 : 400,
                  transition: 'all 150ms var(--ease)',
                }}
              >
                <NavTabIcon tab={tab} Icon={Icon} size={20} strokeWidth={1.5} />
                {t(meta.labelKey)}
              </button>
            );
          })}
        </nav>

        {/* Main content â€” paddingRight reserves space for fixed SmartSearch overlay top-right.
            Agent tab: fixed height so AgentShell's 100% height resolves against a real
            constraint (not an unbounded min-height) and the sidebar list can scroll
            internally rather than causing page scroll. */}
        <main style={{
          flex: 1,
          minWidth: 0, // TICKET-015 follow-up: without this, flex item grows to its content's intrinsic width when a child (e.g. PipelineView's hero stats row + kanban) is wider than the flex allocation, pushing right-side header buttons off the viewport.
          marginLeft: 240,
          ...(activeTab === 'agent'
            ? { height: '100vh', overflow: 'hidden' }
            : { minHeight: '100vh', overflowX: 'hidden' }),
        }}>
          {children}
        </main>
      </div>
    );
  }

  if (isTablet) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        {searchOverlay}
        <nav style={{
          width: 64, flexShrink: 0, background: 'var(--glass)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRight: '1px solid var(--bdr)', padding: '16px 8px',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
          position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 300,
        }}>
          {/* Logo â€” icon only */}
          <div style={{
            width: 36, height: 36, borderRadius: 8, background: 'var(--navy-surface)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--on-navy)', fontSize: 12, fontWeight: 700, marginBottom: 12,
            flexShrink: 0,
          }}>AC</div>

          {/* TICKET-205: the airplane (offline) button was missing from the
              tablet rail entirely â€” restore it, plus the pipeline-only lead
              sync. Panel opens to the right (into content) in this narrow rail. */}
          <OfflineModeButton align="left" />
          {showLeadSync && <LeadSyncButton />}
          {showLeadManage && <ComposeEmailButton />}
            {showLeadManage && <RejectedLeadsButton />}

          {/* Global search trigger â€” see desktop sidebar comment. */}
          <SmartSearchOverlay />

          {tabs.map(tab => {
            const meta = TAB_META[tab];
            const active = activeTab === tab;
            const Icon = meta.icon;
            return (
              <button
                key={tab}
                onClick={() => onTabChange(tab)}
                title={t(meta.labelKey)}
                aria-label={t(meta.labelKey)}
                data-testid={`nav-tab-${tab}`}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 44, height: 44, borderRadius: 10,
                  background: active ? 'var(--accent-bg)' : 'transparent',
                  color: active ? 'var(--accent)' : 'var(--t2)',
                  transition: 'all 150ms var(--ease)',
                }}
              >
                <NavTabIcon tab={tab} Icon={Icon} size={20} strokeWidth={1.5} />
              </button>
            );
          })}
          {signOutIconBtn}
        </nav>
        {/* Agent tab: fixed height so sidebar list scrolls internally, not page-scroll. */}
        <main style={{
          flex: 1,
          minWidth: 0, // TICKET-015 follow-up: see desktop main above.
          marginLeft: 64,
          ...(activeTab === 'agent'
            ? { height: '100vh', overflow: 'hidden' }
            : { minHeight: '100vh', overflowX: 'hidden' }),
        }}>
          {children}
        </main>
      </div>
    );
  }

  // Mobile: bottom tab bar with More drawer
  const visibleCount = MOBILE_VISIBLE_COUNT[role] || 4;
  const visibleTabs = tabs.slice(0, visibleCount);
  const moreTabs = tabs.slice(visibleCount);
  const hasMore = moreTabs.length > 0;
  const isMoreActive = hasMore && moreTabs.includes(activeTab);

  return (
    <div style={{
      minHeight: '100vh',
      // Mobile safe-area: iOS status bar + notch must not overlap the top of
      // the content. Without paddingTop the Focus filter pills + search/mic
      // icons crammed up against the iOS status bar (13:11 overlap bug
      // reported 2026-04-08 PM by CEO with screenshot).
      paddingTop: 'env(safe-area-inset-top, 0px)',
      paddingBottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
    }}>
      {/* Mobile top bar â€” in-flow (not fixed) so view content pushes down
          naturally instead of being covered by the old fixed overlay.
          Fixed 2026-04-08 PM after CEO reported the search button covering
          Projectsâ†’Bundles controls, Pipeline "New Opportunity" button, and
          cramping the Focus filter row. Contents: build identity on the
          left, search button on the right. Voice mic stays inside each
          view (view-specific context). */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '4px 12px 4px 14px', gap: 8,
        background: 'var(--card)', borderBottom: '1px solid var(--bdr)',
        minHeight: 48,
      }}>
        <div style={{
          fontSize: 10, color: 'var(--tm)', fontWeight: 500,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : ''} · {typeof __APP_SHA__ !== 'undefined' ? __APP_SHA__ : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {showLeadSync && <LeadSyncButton />}
          {showLeadManage && <ComposeEmailButton />}
            {showLeadManage && <RejectedLeadsButton />}
          <OfflineModeButton />
          <SmartSearchOverlay />
        </div>
      </div>
      {children}

      {/* More drawer â€” slides up from bottom */}
      {moreOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setMoreOpen(false)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (() => setMoreOpen(false))(e); } }}
            style={{
              position: 'fixed', inset: 0, zIndex: 150,
              background: 'rgba(0,0,0,0.3)',
              transition: 'opacity 150ms',
            }}
          />
          {/* Drawer */}
          <div style={{
            position: 'fixed', bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
            left: 0, right: 0, zIndex: 200,
            background: 'var(--card)', borderRadius: '16px 16px 0 0',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.15)',
            padding: '16px 12px 8px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, padding: '0 4px' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>{t('nav.more')}</span>
              <button onClick={() => setMoreOpen(false)} style={{ padding: 8, background: 'none', color: 'var(--tm)' }}>
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
              {moreTabs.map(tab => {
                const meta = TAB_META[tab];
                if (!meta) return null;
                const active = activeTab === tab;
                const Icon = meta.icon;
                return (
                  <button
                    key={tab}
                    onClick={() => { onTabChange(tab); setMoreOpen(false); }}
                    data-testid={`nav-tab-${tab}`}
                    aria-label={t(meta.labelKey)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      padding: '12px 4px', borderRadius: 12, minHeight: 64, background: 'none',
                      color: active ? 'var(--accent)' : 'var(--t2)',
                    }}
                  >
                    <NavTabIcon tab={tab} Icon={Icon} size={22} strokeWidth={1.5} />
                    <span style={{ fontSize: 11, fontWeight: active ? 600 : 400 }}>{t(meta.labelKey)}</span>
                  </button>
                );
              })}
            </div>
            {/* Sign out — mobile lives in the More drawer (CEO 2026-07-02). */}
            <div style={{ padding: '10px 4px 4px', marginTop: 6, borderTop: '1px solid var(--bdr)' }}>{signOutBtn}</div>
          </div>
        </>
      )}

      {/* Bottom tab bar */}
      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
        height: 'calc(56px + env(safe-area-inset-bottom, 0px))',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        background: 'var(--card)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderTop: '1px solid var(--bdr)',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-around',
      }}>
        {visibleTabs.map(tab => {
          const meta = TAB_META[tab];
          if (!meta) return null;
          const active = activeTab === tab;
          const Icon = meta.icon;
          return (
            <button
              key={tab}
              onClick={() => onTabChange(tab)}
              data-testid={`nav-tab-${tab}`}
              aria-label={t(meta.labelKey)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                padding: '6px 0', minWidth: 56, minHeight: 44, background: 'none',
                color: active ? 'var(--accent)' : 'var(--t2)',
                transition: 'all 150ms var(--ease)',
              }}
            >
              <NavTabIcon tab={tab} Icon={Icon} size={22} strokeWidth={1.5}
                style={{ transform: active ? 'scale(1.1)' : 'scale(1)', transition: 'transform 150ms' }}
              />
              <span style={{ fontSize: 10, fontWeight: active ? 600 : 400 }}>{t(meta.labelKey)}</span>
            </button>
          );
        })}
        {hasMore && (
          <button
            onClick={() => setMoreOpen(v => !v)}
            data-testid="nav-tab-more"
            aria-label={t('nav.more')}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              padding: '6px 0', minWidth: 56, minHeight: 44, background: 'none',
              color: isMoreActive ? 'var(--accent)' : 'var(--t2)',
              transition: 'all 150ms var(--ease)',
            }}
          >
            <MoreHorizontal size={22} strokeWidth={1.5}
              style={{ transform: moreOpen ? 'scale(1.1)' : 'scale(1)', transition: 'transform 150ms' }}
            />
            <span style={{ fontSize: 10, fontWeight: isMoreActive ? 600 : 400 }}>{t('nav.more')}</span>
          </button>
        )}
      </nav>
    </div>
  );
}

export { TAB_CONFIG, TAB_META };
